<script setup lang="ts">
import { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import ProductComment from '~/models/Product/ProductComment'
import { useAuthStore } from '~/stores/auth'

const props = defineProps({
    comment: {
        type: Object as PropType<ProductComment>,
        required: true,
    },
    searchQueries: {
        type: Array as PropType<string[]>,
        required: false,
        default: () => [],
    },
})

const emit = defineEmits(['setLoading'])

const showIsUsefulMessage = ref(false)
const isUsefulMessage = ref('')
const isShownOriginalText = ref(false)

const locale = useI18n().locale

const isWordPartOfSearchQuery = (word: string) => {
    return props.searchQueries.some((query) => word.toLowerCase().normalize('NFC').includes(query))
}

const findTextInSearch = (pros: string[], cons: string[], text: string, variant: string) => {
    if (props.searchQueries.length) {
        const data = [pros, cons, text, variant]
        for (let i = 0; i < data.length; i++) {
            const prosWordFormat = data.toString()
            const prosWord = prosWordFormat
                .replace(/\r?\n|\r/g, ' ')
                .trim()
                .split(/ /g)
            for (const word of prosWord) {
                if (isWordPartOfSearchQuery(word)) {
                    return true
                }
            }
        }
    }

    return false
}

const formatDate = (date: string | undefined) => {
    return date?.replace(/(\d{2})-(\d{2})-(\d{4})/, '$1.$2.$3')
}

const rateComment = async (comment: ProductComment, isUseful: boolean) => {
    const authStore = useAuthStore()
    const accessToken = authStore.accessToken
    if (!accessToken) {
        useNuxtApp().$bus.$emit('showModal', 'ask-question-login')

        return
    }

    const data = {
        uid: comment.uid,
        accessToken,
        isUseful,
    }

    emit('setLoading', true)

    try {
        const { likesCount, dislikesCount } = await useNuxtApp().$repository.rateComment(data)

        if (isFinite(likesCount) && isFinite(dislikesCount)) {
            comment.countUseful = likesCount
            comment.countNotUseful = dislikesCount
        }
    } catch {}

    emit('setLoading', false)
}

const isArtificialUntranslatedComment =
    props.comment.isArtificial &&
    props.comment.language &&
    ((props.comment.language === 'cz' && locale.value === 'sk') || (props.comment.language === 'sk' && locale.value === 'cz'))
</script>

<template>
    <div
        data-rating="container"
        class="rating__wrapper"
        :data-uid="comment.uid"
        :class="{
            'product-detail__rating-box--activeSearch': searchQueries.length,
            'product-detail__rating-box--activeSearch-activeItem': findTextInSearch(
                comment.pros,
                comment.cons,
                comment.text,
                comment.productName
            ),
        }"
    >
        <div class="rating__user_info_wrapper">
            <div class="rating__user">
                <div class="rating__user_name">{{ comment.name }}</div>
                <div v-if="comment.isVerifiedPurchase" class="rating__user_verified">
                    <img
                        src="/nuxt-assets/images/icons/confirm.svg"
                        :alt="$t('productDetail.rating.employee')"
                        class="product-detail__rating-img mr-2"
                        width="16"
                        height="16"
                    />
                    {{ $t('productDetail.rating.employee') }}
                </div>
                <div v-else class="rating__user_no-verified">
                    {{ $t('productDetail.rating.noEmployeeReview') }}
                </div>
                <div class="rating__info_date" v-html="formatDate(comment.updatedAt)"></div>
            </div>
            <div class="rating__info">
                <div v-if="comment.isRecommend" class="rating__info_recommend">
                    <span class="ratingRecommend">
                        <img
                            :alt="$t('productDetail.rating.recommend')"
                            src="/nuxt-assets/images/icons/like.svg"
                            class="product-detail__rating-content-img"
                            width="11"
                            height="11"
                        />
                    </span>
                    {{ $t('productDetail.rating.recommend') }}
                </div>
                <div class="rating__info_date" v-html="formatDate(comment.updatedAt)"></div>
            </div>
        </div>
        <div class="rating__content_wrapper">
            <div class="rating__content_stars_wrapper">
                <div class="rating__stars_wrapper">
                    <ObjectsRating :rating="comment.rating" :box="true" />
                    <template v-if="comment.productName">
                        <div v-if="searchQueries.length">
                            <span class="rating__variant_name">
                                {{ $t('productDetail.rating.variant') }}
                                <span
                                    v-for="(word, i) in comment.productName
                                        .replace(/\r?\n|\r/g, ' ')
                                        .trim()
                                        .split(/ /g)"
                                    :key="i"
                                    :class="isWordPartOfSearchQuery(word) ? 'bg-search-term' : ''"
                                >
                                    {{ word }}&nbsp;
                                </span>
                            </span>
                        </div>
                        <div v-else>
                            <span class="rating__variant_name">{{ $t('productDetail.rating.variant') }} {{ comment.productName }}</span>
                        </div>
                    </template>
                </div>
                <div v-if="comment.isRecommend" class="rating__info_recommend">
                    <span class="ratingRecommend">
                        <img
                            :alt="$t('productDetail.rating.recommend')"
                            src="/nuxt-assets/images/icons/like.svg"
                            class="product-detail__rating-content-img"
                            width="11"
                            height="11"
                        />
                    </span>
                    {{ $t('productDetail.rating.recommend') }}
                </div>
            </div>

            <div v-if="comment.isArtificial && comment.language && comment.language !== locale" class="purple-badge text-dark">
                {{ $t(isArtificialUntranslatedComment ? 'comments.commentFrom' : 'comments.automaticallyTranslatedFrom') }} Pilulka.{{
                    comment.language
                }}
            </div>

            <div v-if="comment.pros.length > 0 || comment.cons.length > 0" class="rating__pros_cons">
                <div class="rating__pros">
                    <template v-for="(pros, index) in comment.pros">
                        <div v-if="pros" :key="index" class="rating__pros_item">
                            <img src="/nuxt-assets/images/icons/plus.svg" alt="plus" width="16" height="16" />
                            <div v-if="searchQueries.length">
                                <span
                                    v-for="(pro, i) in pros
                                        .replace(/\r?\n|\r/g, ' ')
                                        .trim()
                                        .split(/ /g)"
                                    :key="i"
                                    :class="isWordPartOfSearchQuery(pro) ? 'bg-search-term' : ''"
                                >
                                    {{ pro }}&nbsp;
                                </span>
                            </div>
                            <div v-else>{{ pros }}</div>
                        </div>
                    </template>
                </div>
                <div class="rating__cons">
                    <template v-for="(cons, index) in comment.cons">
                        <div v-if="cons" :key="index" class="rating__cons_item">
                            <img src="/nuxt-assets/images/icons/minus.svg" alt="minus" width="16" height="16" />
                            <div v-if="searchQueries.length" class="col-10">
                                <span
                                    v-for="(con, i) in cons
                                        .replace(/\r?\n|\r/g, ' ')
                                        .trim()
                                        .split(/ /g)"
                                    :key="i"
                                    :class="isWordPartOfSearchQuery(con) ? 'bg-search-term' : ''"
                                >
                                    {{ con }}&nbsp;
                                </span>
                            </div>
                            <div v-else class="col-10">{{ cons }}</div>
                        </div>
                    </template>
                </div>
            </div>

            <div v-if="searchQueries.length" class="rating__rating_content">
                <span
                    v-for="(word, i) in comment.text
                        .replace(/\r?\n|\r/g, ' ')
                        .trim()
                        .split(/ /g)"
                    :key="i"
                    :class="isWordPartOfSearchQuery(word) ? 'bg-search-term' : ''"
                >
                    {{ word }}&nbsp;
                </span>
            </div>
            <div v-else class="rating__rating_content">
                {{ comment.text }}

                <div v-if="isShownOriginalText">
                    <div class="purple-badge text-dark mt-4 mb-2">
                        {{ $t('comments.originalRating') }}
                    </div>

                    <div v-if="comment.originalPros.length > 0 || comment.originalCons.length > 0" class="row mt-3">
                        <div class="col-12 col-md-6">
                            <template v-for="(pros, index) in comment.originalPros">
                                <div v-if="pros" :key="index" class="d-flex align-items-start mb-1 fs-16">
                                    <img src="/nuxt-assets/images/icons/plus.svg" alt="plus" class="me-2 my-auto" width="13" height="13" />
                                    <div v-if="searchQueries.length" class="col-10">
                                        <span
                                            v-for="(pro, i) in pros
                                                .replace(/\r?\n|\r/g, ' ')
                                                .trim()
                                                .split(/ /g)"
                                            :key="i"
                                            :class="isWordPartOfSearchQuery(pro) ? 'bg-search-term' : ''"
                                        >
                                            {{ pro }}&nbsp;
                                        </span>
                                    </div>
                                    <div v-else class="col-10">{{ pros }}</div>
                                </div>
                            </template>
                        </div>
                        <div class="col-12 col-md-6">
                            <template v-for="(cons, index) in comment.originalCons">
                                <div v-if="cons" :key="index" class="d-flex align-items-start mb-1 fs-16">
                                    <img
                                        src="/nuxt-assets/images/icons/minus.svg"
                                        alt="minus"
                                        class="me-2 my-auto"
                                        width="13"
                                        height="13"
                                    />
                                    <div v-if="searchQueries.length" class="col-10">
                                        <span
                                            v-for="(con, i) in cons
                                                .replace(/\r?\n|\r/g, ' ')
                                                .trim()
                                                .split(/ /g)"
                                            :key="i"
                                            :class="isWordPartOfSearchQuery(con) ? 'bg-search-term' : ''"
                                        >
                                            {{ con }}&nbsp;
                                        </span>
                                    </div>
                                    <div v-else class="col-10">{{ cons }}</div>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div>
                        {{ comment.originalText }}
                    </div>
                </div>

                <div
                    v-if="comment.originalText?.length && comment.originalText !== comment.text && !isShownOriginalText"
                    class="text-decoration-underline cursor-point text-brand mt-2"
                    @click="isShownOriginalText = true"
                >
                    {{ $t('comments.showOriginal') }}
                </div>
            </div>

            <div class="rating__vote_wrapper">
                <div class="product-prev__like-box__tooltip product-prev__like-box__tooltip--hidden"></div>
                {{ $t('rating.useful') }}
                <button class="rating__vote_button yes" @click="rateComment(comment, true)">
                    <img src="/nuxt-assets/images/icons/like.svg" :alt="$t('productDetail.rating.yes')" class="mx-1" />
                    <span>{{ $t('productDetail.rating.yes') }}</span> ({{ comment.countUseful }})
                </button>
                <button class="rating__vote_button no" @click="rateComment(comment, false)">
                    <img src="/nuxt-assets/images/icons/dislike.svg" :alt="$t('productDetail.rating.no')" class="mx-1" />
                    <span>{{ $t('productDetail.rating.no') }}</span> ({{ comment.countNotUseful }})
                </button>

                <div
                    v-if="showIsUsefulMessage"
                    :ref="'like-box-tooltip-' + comment.uid"
                    class="tooltip-usefulmessage"
                    v-html="isUsefulMessage"
                ></div>
            </div>
            <div class="rating__usefull_wrapper">
                <a class="rating__usefull_link" :href="$t('productDetail.rating.process.url')">
                    {{ $t('productDetail.rating.process.text') }}
                </a>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.purple-badge {
    background: $color-purple-light;
    padding: 0.35rem 0.75rem;
    font-size: 14px;
    border-radius: 2rem;
    display: inline-block;
}

.rating {
    &__wrapper {
        border-bottom: 1px solid $color-grey-dark;
        padding-bottom: 0.5rem;
        padding-top: 1rem;

        @include screen-md {
            display: flex;
            gap: 2rem;
        }
    }

    &__user {
        .rating__info_date {
            display: none;
            text-align: center;

            @include screen-md {
                display: block;
                margin: 0.5rem auto;
            }
        }
    }

    &__user_info_wrapper {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1rem;
    }

    &__info {
        @include screen-md {
            display: none;
        }
    }

    &__user_name {
        font-size: 16px;
        color: $color-dark;
        margin: 0.5rem auto;

        @include screen-md {
            text-align: center;
        }
    }

    &__user_verified {
        color: $color-green-dark;
        font-size: 12px;
        display: flex;
        align-items: center;

        img {
            margin-right: 0.5rem;
        }
    }

    &__user_no-verified {
        color: $color-grey-extra-dark;
        font-size: 12px;
        display: flex;
        align-items: center;

        img {
            margin-right: 0.5rem;
        }
    }

    &__info_recommend {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: $color-green-dark;
        margin: 0.5rem auto;
        font-weight: 700;

        .ratingRecommend {
            display: flex;
            justify-content: center;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background-color: $color-green-light;
            margin-right: 0.5rem;

            img {
                padding-bottom: 2px;
            }
        }
    }

    &__content_wrapper {
        @include screen-md {
            flex: 1;
        }
    }

    &__content_stars_wrapper {
        @include screen-md {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        .rating__info_recommend {
            display: none;

            @include screen-md {
                display: flex;
                margin: 0;
                font-size: 14px;
            }

            .ratingRecommend {
                @include screen-md {
                    width: 26px;
                    height: 26px;
                    align-items: center;
                }

                img {
                    @include screen-md {
                        width: 16px;
                        height: 16px;
                    }
                }
            }
        }
    }

    &__info_date {
        font-size: 12px;
        text-align: right;
        color: $color-dark;
    }

    &__stars_wrapper {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1rem;
        align-items: center;
        gap: 1rem;

        @include screen-md {
            align-items: flex-start;
            flex-direction: column;
            gap: 0.25rem;
        }
    }

    &__variant_name {
        font-size: 12px;
        color: $color-grey-extra-dark;
        margin: 0.5rem auto;
    }

    &__rating_content {
        font-size: 14px;
        color: $color-dark;
        margin: 1rem auto;
    }

    &__pros_cons {
        display: flex;
        flex-direction: column;
    }

    &__cons_item,
    &__pros_item {
        font-size: 14px;
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        img {
            margin-right: 0.5rem;
        }
    }

    &__vote_wrapper {
        font-size: 12px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 1rem;

        @include screen-md {
            justify-content: right;
            margin-bottom: 0.5rem;
        }
    }

    &__vote_button {
        background: transparent;
        border: 1px solid transparent;
        color: #333;
        cursor: pointer;
        font-weight: 400;

        span {
            text-decoration: underline;
        }

        &.yes {
            img {
                margin-bottom: 2px;
            }
        }

        &.no {
            border-left: 1px solid $color-grey-dark;
        }
    }

    &__usefull_wrapper {
        display: flex;
        justify-content: center;

        @include screen-md {
            justify-content: right;
        }
    }

    &__usefull_link {
        font-size: 12px;
        color: $color-grey-extra-dark;
        text-decoration: underline;
    }
}
</style>
